import { Link } from "wouter";
import { useI18n } from "@/lib/i18n";

/**
 * Privacy policy page. Required for App Store / Google Play submission and
 * referenced from the OAuth consent screens of every integration provider
 * (GitHub, Google, Slack, Microsoft). Public route — must be reachable
 * without sign-in so OAuth review can crawl it.
 *
 * Bilingual EN/AR with RTL on the Arabic side. Mirrors the warm coral/sand
 * palette of the rest of the app to stay on-brand.
 */
export default function Privacy() {
  const { t, locale } = useI18n();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container max-w-3xl py-12">
        <Link
          href="/"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {locale === "ar" ? "→" : "←"} {t.privacyBackToApp}
        </Link>

        <h1 className="mt-6 text-4xl font-serif font-semibold tracking-tight">
          {t.privacyTitle}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t.privacyLastUpdated}
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-7">
          {locale === "ar" ? <ArabicPolicy /> : <EnglishPolicy />}
        </div>
      </div>
    </div>
  );
}

/* ----------------------- English copy ----------------------- */

function EnglishPolicy() {
  return (
    <>
      <Section title="Who we are">
        <p>
          Receipt is a work-week dashboard that auto-generates structured
          activity summaries (&ldquo;Receipts&rdquo;) from the integrations you
          connect. This page describes what data Receipt collects, how it is
          stored, who can see it, and how you can delete it.
        </p>
      </Section>

      <Section title="What we collect">
        <ul className="list-disc ms-6 space-y-2">
          <li>
            <strong>Account profile</strong>: your name, email, and the OpenID
            issued by your sign-in provider (Manus OAuth or Google).
          </li>
          <li>
            <strong>Connected integrations</strong>: when you connect GitHub,
            Google Calendar, Slack, Microsoft 365, or an Exchange on-premises
            mailbox, Receipt stores the OAuth access &amp; refresh tokens (or,
            for Exchange, your encrypted credentials) and a pointer to the
            account on the third party. Tokens are stored encrypted at rest.
          </li>
          <li>
            <strong>Signals</strong>: lightweight summaries of the events these
            integrations emit &mdash; commit titles, calendar event titles,
            Slack message snippets, blocker text. Receipt does not store full
            email bodies, full message threads, or attachments.
          </li>
          <li>
            <strong>Receipts</strong>: the daily and weekly summaries Receipt
            drafts from your signals, plus any edits you make to them.
          </li>
          <li>
            <strong>Voice memos</strong>: when you record a voice memo, the
            audio file is uploaded to Manus storage and transcribed by
            Whisper; the transcript is stored with the memo. Audio files are
            kept until you delete the memo or your account.
          </li>
          <li>
            <strong>Karma reactions</strong> teammates leave on your receipts.
          </li>
          <li>
            <strong>Push tokens</strong> (iOS / Android / Web) if you opt in
            to notifications.
          </li>
          <li>
            <strong>Audit log</strong>: an internal record of security-relevant
            actions (sign-in, sign-out everywhere, account deletion).
          </li>
        </ul>
      </Section>

      <Section title="How we use it">
        <p>
          Your data is used solely to render <em>your</em> dashboard, draft
          your receipts, share them with teammates you have explicitly added
          to a team, and produce the Friday OG share card if you request one.
          Receipt does not sell your data and does not run advertising.
          Receipt itself does not submit your data to any model-training
          programme; what an external AI provider does with content once it is
          sent is that provider&rsquo;s own matter, described below.
        </p>
        <p className="mt-3">
          Some features send selected content to an external AI provider. When
          Receipt drafts a receipt, your signals are sent to a large language
          model. When you ask for a Your Week reflection, that week&rsquo;s
          selected private context &mdash; which can include unapproved drafts
          &mdash; is sent to OpenAI, and only when you press the button.
          Neither request reads another member&rsquo;s records, roster or
          participation data, or Team Direction text. What is sent is your own
          material &mdash; and your own receipt text can itself contain wording
          that reached it from elsewhere, such as a meeting title or a message
          you were mentioned in.
        </p>
        <p className="mt-3">
          Receipt does not store the model&rsquo;s response for Your Week
          reflections: it is returned to you and held only in your browser
          session, so closing or reloading the page clears it. What the provider
          does with the content once it is sent is governed by the provider
          arrangement in force for this deployment, not by Receipt, and Receipt
          does not claim a particular provider retention period here.
        </p>
      </Section>

      <Section title="Microphone permission">
        <p>
          Receipt requests microphone access only when you tap the voice
          capture button. Audio is recorded locally in your browser or native
          shell, uploaded to Manus storage over HTTPS, transcribed by
          Whisper, then routed back to you. Receipt never starts recording
          without an explicit tap and never streams microphone audio in the
          background.
        </p>
      </Section>

      <Section title="Third parties">
        <p>
          Receipt connects to third parties only at your direction. The
          integrations Receipt currently supports are GitHub, Google Calendar,
          Slack, Microsoft 365 (via Microsoft Graph), and Exchange
          on-premises (via EWS). Each is governed by its own privacy policy.
          You can revoke any integration at any time from the Settings dialog
          or from the third party&rsquo;s account console; revocation deletes
          the stored tokens and stops further sync.
        </p>
      </Section>

      <Section title="Where data lives">
        <p>
          Account data, receipts, signals, and tokens are stored in a managed
          MySQL/TiDB database. Voice memos and the Friday share card are
          stored in Manus&rsquo;s S3-backed object store. All traffic between
          the app and the server is encrypted in transit (TLS 1.2+).
        </p>
      </Section>

      <Section title="Retention">
        <p>
          Signals older than 90 days are eligible for automatic pruning so
          the dashboard stays focused on your current context. Receipts are
          kept until you delete them or your account. Audit log rows are
          retained for compliance even after account deletion, but the
          user-id column is anonymised so the row no longer identifies you.
        </p>
      </Section>

      <Section title="Your rights">
        <ul className="list-disc ms-6 space-y-2">
          <li>
            <strong>Access &amp; export</strong>: every receipt and signal
            Receipt holds about you is visible inside the app.
          </li>
          <li>
            <strong>Edit</strong>: every receipt is editable inline before or
            after approval.
          </li>
          <li>
            <strong>Disconnect an integration</strong>: from the integrations
            tray on the canvas; the stored tokens are deleted immediately.
          </li>
          <li>
            <strong>Sign out everywhere</strong>: from Settings &rarr; Sign
            out everywhere; this invalidates every active session for your
            account.
          </li>
          <li>
            <strong>Delete your account</strong>: from Settings &rarr; Delete
            account. This permanently removes your profile, receipts,
            signals, voice memos, integrations, push tokens, streaks,
            preferences, team memberships, and karma reactions you gave or
            received. The audit log is anonymised. The deletion is immediate
            and cannot be undone.
          </li>
        </ul>
      </Section>

      <Section title="Children">
        <p>
          Receipt is a workplace productivity tool and is not directed at
          children under 13. We do not knowingly collect data from children.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          For privacy questions, data export requests, or anything else,
          contact the project owner of your Receipt workspace.
        </p>
      </Section>
    </>
  );
}

/* ----------------------- Arabic copy ----------------------- */

function ArabicPolicy() {
  return (
    <>
      <Section title="من نحن">
        <p>
          Receipt هي لوحة تحكم لأسبوع العمل تنشئ ملخصات نشاط منظَّمة
          (&laquo;إيصالات&raquo;) من التكاملات التي تربطها. توضح هذه الصفحة
          البيانات التي تجمعها Receipt، وكيف تُخزَّن، ومن يمكنه الوصول إليها،
          وكيف يمكنك حذفها.
        </p>
      </Section>
      <Section title="ما الذي نجمعه">
        <ul className="list-disc me-6 space-y-2">
          <li>
            <strong>الملف الشخصي</strong>: اسمك، بريدك، ومعرّف OpenID الصادر
            من مزوّد تسجيل الدخول.
          </li>
          <li>
            <strong>التكاملات</strong>: عند ربط GitHub أو Google Calendar أو
            Slack أو Microsoft 365 أو Exchange الداخلي، تُخزَّن رموز الوصول
            والتحديث (أو بيانات اعتماد Exchange مشفّرة) ومرجع للحساب لدى
            الطرف الثالث. كل الرموز مشفّرة في حالة السكون.
          </li>
          <li>
            <strong>الإشارات</strong>: ملخصات خفيفة لما تُصدره تلك
            التكاملات: عناوين الالتزامات، عناوين أحداث التقويم، مقتطفات
            رسائل Slack، وعوائق العمل. لا تُخزَّن نصوص البريد الكاملة ولا
            خيوط المحادثات الكاملة ولا المرفقات.
          </li>
          <li>
            <strong>الإيصالات</strong>: الملخصات اليومية والأسبوعية التي
            تُصاغها Receipt من إشاراتك، بالإضافة إلى أي تعديلات تُجريها.
          </li>
          <li>
            <strong>الملاحظات الصوتية</strong>: عند تسجيل ملاحظة، يُرفع
            الملف إلى تخزين Manus ويُفرَّغ نصياً عبر Whisper.
          </li>
          <li>
            <strong>تفاعلات الكارما</strong>، ورموز الإشعارات، وسجلّ
            تدقيق داخلي.
          </li>
        </ul>
      </Section>
      <Section title="كيف نستخدمها">
        <p>
          تُستخدم بياناتك لعرض لوحتك أنت، ولصياغة إيصالاتك، ولمشاركتها مع
          زملاء أضفتهم صراحةً إلى فريق. لا يبيع Receipt بياناتك ولا يعرض
          إعلانات. ولا يقدّم Receipt نفسه بياناتك لأي برنامج تدريب نماذج، أمّا
          ما يفعله مزوّد خارجي بالمحتوى بعد إرساله فشأنه هو، وهو موضّح أدناه.
        </p>
        <p className="mt-3">
          ترسل بعض الميزات محتوى مختاراً إلى مزوّد ذكاء اصطناعي خارجي. عند
          صياغة إيصال تُرسل إشاراتك إلى نموذج لغوي. وعند طلبك تأمّلاً في
          &laquo;أسبوعك&raquo; يُرسل سياق ذلك الأسبوع الخاص، وقد يشمل مسودات
          غير معتمدة، إلى OpenAI، وذلك فقط عند ضغطك الزر. ولا يقرأ أيّ من
          الطلبين سجلّات عضو آخر ولا بيانات العضوية أو المشاركة ولا نصوص
          &laquo;توجيه الفريق&raquo;. المُرسل هو مادّتك أنت، وقد يحتوي نصّ
          إيصالك نفسه على صياغة وصلت إليه من مكان آخر، كعنوان اجتماع أو رسالة
          ذُكرت فيها.
        </p>
        <p className="mt-3">
          لا يخزّن Receipt نص التأمّل الناتج: يُعاد إليك ويبقى في جلسة
          المتصفّح وحدها، فإغلاق الصفحة أو تحديثها يمحوه. أمّا ما يفعله
          المزوّد بالمحتوى بعد إرساله فتحكمه ترتيبات المزوّد المعمول بها لهذا
          التشغيل لا Receipt، ولا يدّعي Receipt هنا مدة احتفاظ بعينها لدى
          المزوّد.
        </p>
      </Section>
      <Section title="إذن الميكروفون">
        <p>
          يطلب Receipt الوصول إلى الميكروفون فقط عند ضغطك زر التسجيل الصوتي.
          يُسجَّل الصوت محلياً في متصفّحك أو في التطبيق، ثم يُرفع إلى تخزين
          Manus عبر HTTPS، ويُفرَّغ نصياً بواسطة Whisper، ثم يعود إليك. لا
          يبدأ Receipt التسجيل دون ضغطة صريحة، ولا ينقل صوت الميكروفون في
          الخلفية.
        </p>
      </Section>

      <Section title="أطراف ثالثة">
        <p>
          لا يتصل Receipt بأي طرف ثالث إلا بتوجيه منك. التكاملات المدعومة
          حالياً هي GitHub وGoogle Calendar وSlack وMicrosoft 365 (عبر
          Microsoft Graph) وExchange المحلي (عبر EWS)، ويخضع كل منها لسياسة
          الخصوصية الخاصة به. يمكنك إلغاء أي تكامل في أي وقت من نافذة
          الإعدادات أو من لوحة حساب الطرف الثالث، وعندها تُحذف الرموز
          المخزّنة ويتوقّف الاستيراد.
        </p>
      </Section>

      <Section title="أين تُخزَّن البيانات">
        <p>
          تُخزَّن بيانات الحساب والإيصالات والإشارات والرموز في قاعدة بيانات
          MySQL/TiDB مُدارة. أمّا المقاطع الصوتية وبطاقة المشاركة الأسبوعية
          فتُخزَّن في مخزن الكائنات التابع لـ Manus والمبني على S3. وكل حركة
          بين التطبيق والخادم مشفّرة أثناء النقل (TLS 1.2 فأعلى).
        </p>
      </Section>

      <Section title="مدة الاحتفاظ">
        <p>
          الإشارات الأقدم من 90 يوماً مؤهّلة للتنظيف التلقائي كي تبقى لوحتك
          مركّزة على سياقك الحالي. وتبقى الإيصالات حتى تحذفها أنت أو تحذف
          حسابك. أمّا صفوف سجلّ التدقيق فيُحتفظ بها لأغراض الامتثال حتى بعد
          حذف الحساب، لكن عمود معرّف المستخدم يُجهَّل فلا يعود الصف يدلّ
          عليك.
        </p>
      </Section>

      <Section title="حقوقك">
        <ul className="list-disc me-6 space-y-2">
          <li>
            <strong>التحرير</strong>: كل إيصال قابل للتحرير قبل أو بعد
            الاعتماد.
          </li>
          <li>
            <strong>قطع الاتصال</strong>: من درج التكاملات في اللوحة. تُحذف
            الرموز فوراً.
          </li>
          <li>
            <strong>تسجيل الخروج من كل الأجهزة</strong>: من الإعدادات.
          </li>
          <li>
            <strong>حذف الحساب</strong>: من الإعدادات &laquo;حذف الحساب&raquo;.
            يُزيل ملفّك الشخصي وإيصالاتك وإشاراتك وملاحظاتك الصوتية وتكاملاتك
            ورموز الإشعارات وسلاسل النشاط والتفضيلات والعضويات وتفاعلات
            الكارما بشكل دائم. هذا الإجراء فوري ولا يمكن التراجع عنه.
          </li>
        </ul>
      </Section>
      <Section title="الأطفال">
        <p>
          Receipt أداة إنتاجية لأماكن العمل وليست موجّهة إلى الأطفال دون سنّ
          الثالثة عشرة، ولا نجمع عن قصد بيانات من الأطفال.
        </p>
      </Section>

      <Section title="جهات الاتصال">
        <p>للأسئلة المتعلقة بالخصوصية، يرجى التواصل مع مالك مساحة عملك في Receipt.</p>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-serif font-semibold mb-3">{title}</h2>
      <div className="text-foreground/85">{children}</div>
    </section>
  );
}
